import { useState, useEffect } from 'react';
import { Card, Button, Badge, Modal as BootstrapModal, Form, Alert, Table, Row, Col, InputGroup } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { WithdrawalService } from '../../services/api/withdrawals';
import { useConfirmation } from '../../hooks/useConfirmation';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { Drawer } from '../../theme/components';

const WithdrawalAdminPanel = () => {
  const [activeView, setActiveView] = useState('pending'); // 'pending', 'approved', 'rejected'
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para busca
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
    pendingAmount: 0
  });

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetails, setRequestDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const { modalProps, confirmAction } = useConfirmation();

  useEffect(() => {
    loadPendingRequests();
    loadApprovedRequests();
    loadRejectedRequests();
  }, []);

  // Calcular estatísticas
  useEffect(() => {
    const total = pendingRequests.length + approvedRequests.length + rejectedRequests.length;
    const totalAmount = [...pendingRequests, ...approvedRequests, ...rejectedRequests]
      .reduce((sum, req) => sum + (req.amount || 0), 0);
    const pendingAmount = pendingRequests.reduce((sum, req) => sum + (req.amount || 0), 0);
    
    setStats({
      total,
      pending: pendingRequests.length,
      approved: approvedRequests.length,
      rejected: rejectedRequests.length,
      totalAmount,
      pendingAmount
    });
  }, [pendingRequests, approvedRequests, rejectedRequests]);

  // Função para obter a lista atual baseada na view ativa
  const getCurrentRequests = () => {
    let requests = [];
    
    switch (activeView) {
      case 'pending':
        requests = pendingRequests;
        break;
      case 'approved':
        requests = approvedRequests;
        break;
      case 'rejected':
        requests = rejectedRequests;
        break;
      default:
        requests = pendingRequests;
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      requests = requests.filter(req => 
        req.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.id.toString().includes(searchTerm)
      );
    }

    return requests;
  };

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await WithdrawalService.getPendingRequests();
      setPendingRequests(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar solicitações pendentes:', error);
      setError('Erro ao carregar solicitações pendentes');
    } finally {
      setLoading(false);
    }
  };

  const loadApprovedRequests = async () => {
    try {
      const response = await WithdrawalService.getApprovedRequests();
      setApprovedRequests(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar solicitações aprovadas:', error);
    }
  };

  const loadRejectedRequests = async () => {
    try {
      const response = await WithdrawalService.getRejectedRequests();
      setRejectedRequests(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar solicitações rejeitadas:', error);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      setError('');
      
      const response = await WithdrawalService.approveRequest(selectedRequest.id, {
        admin_notes: adminNotes
      });

      if (response.success) {
        setSuccess('Solicitação aprovada com sucesso!');
        await loadPendingRequests();
        await loadApprovedRequests();
      setShowApproveModal(false);
      resetModalStates();
      } else {
        setError(response.message || 'Erro ao aprovar solicitação');
      }
    } catch (error) {
      console.error('Erro ao aprovar solicitação:', error);
      setError('Erro ao aprovar solicitação');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      setError('Motivo da rejeição é obrigatório');
      return;
    }

    try {
      setProcessing(true);
      setError('');
      
      const response = await WithdrawalService.rejectRequest(selectedRequest.id, {
        rejection_reason: rejectionReason
      });

      if (response.success) {
        setSuccess('Solicitação rejeitada com sucesso!');
        await loadPendingRequests();
        await loadRejectedRequests();
      setShowRejectModal(false);
      resetModalStates();
      } else {
        setError(response.message || 'Erro ao rejeitar solicitação');
      }
    } catch (error) {
      console.error('Erro ao rejeitar solicitação:', error);
      setError('Erro ao rejeitar solicitação');
    } finally {
      setProcessing(false);
    }
  };

  const loadRequestDetails = async (request) => {
    try {
      setLoadingDetails(true);
      setSelectedRequest(request);
      setShowDetailsDrawer(true);
      
      const response = await WithdrawalService.getRequestDetails(request.id);
      setRequestDetails(response.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      setError('Erro ao carregar detalhes da solicitação');
    } finally {
      setLoadingDetails(false);
    }
  };

  const resetModalStates = () => {
    setSelectedRequest(null);
    setRequestDetails(null);
    setAdminNotes('');
    setRejectionReason('');
    setError('');
  };

  const formatCurrency = (valueInCents) => {
    return CurrencyFormatter.centsToFormatted(valueInCents);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
        <p className="mt-3 text-muted">Carregando solicitações...</p>
      </div>
    );
  }

  return (
    <>
      <Card className="bg-transparent border-0">
        <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            <Icon icon="mdi:account-supervisor" /> 
            Administração - Solicitações de Saque
          </h5>
          <Button
            variant="outline-primary"
            onClick={() => {
              loadPendingRequests();
              loadApprovedRequests();
              loadRejectedRequests();
            }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Carregando...
              </>
            ) : (
              <>
                <Icon icon="mdi:refresh" className="me-1" />
                Atualizar
              </>
            )}
          </Button>
        </Card.Header>

        <Card.Body>
          {/* Alertas */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
              <Icon icon="mdi:check-circle" className="me-2" />
              {success}
            </Alert>
          )}

          {/* Botões de Navegação */}
          <div className="d-flex gap-2 mb-4">
            <Button
              variant={activeView === 'pending' ? 'warning' : 'outline-warning'}
              onClick={() => setActiveView('pending')}
              className="d-flex align-items-center gap-2"
            >
                  <Icon icon="mdi:clock-outline" />
                  Pendentes
              {stats.pending > 0 && (
                <Badge bg="light" text="dark" className="ms-1">
                  {stats.pending}
                </Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'approved' ? 'success' : 'outline-success'}
              onClick={() => setActiveView('approved')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:check-circle" />
              Aprovados
              {stats.approved > 0 && (
                <Badge bg="light" text="dark" className="ms-1">
                  {stats.approved}
                </Badge>
              )}
            </Button>
            
            <Button
              variant={activeView === 'rejected' ? 'danger' : 'outline-danger'}
              onClick={() => setActiveView('rejected')}
              className="d-flex align-items-center gap-2"
            >
              <Icon icon="mdi:close-circle" />
              Rejeitados
              {stats.rejected > 0 && (
                <Badge bg="light" text="dark" className="ms-1">
                  {stats.rejected}
                </Badge>
              )}
            </Button>
          </div>

          {/* Busca */}
          <div className="mb-4">
            <InputGroup>
              <InputGroup.Text>
                <Icon icon="mdi:magnify" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar por nome, email ou ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {/* Tabela de Solicitações */}
          {getCurrentRequests().length === 0 ? (
            <div className="text-center py-5">
              <Icon icon="mdi:inbox-outline" size={64} className="text-muted mb-3" />
              <h6 className="text-muted">
                {activeView === 'pending' ? 'Nenhuma solicitação pendente' :
                 activeView === 'approved' ? 'Nenhuma solicitação aprovada' :
                 'Nenhuma solicitação rejeitada'}
              </h6>
              <p className="text-muted small">
                {activeView === 'pending' ? 'Todas as solicitações foram processadas.' :
                 activeView === 'approved' ? 'Saques aprovados aparecerão aqui.' :
                 'Solicitações rejeitadas aparecerão aqui.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuário</th>
                    <th>Valor</th>
                    <th>Conta Bancária</th>
                    <th>Data Solicitação</th>
                    {activeView === 'approved' && <th>Data Aprovação</th>}
                    {activeView === 'rejected' && <th>Data Rejeição</th>}
                    <th>Observações</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentRequests().map((request) => (
                    <tr key={request.id}>
                      <td className="fw-bold">#{request.id}</td>
                      <td>
                        <div>
                          <div className="fw-semibold">{request.user?.name || request.user_name}</div>
                          <small className="text-muted">{request.user?.email || request.user_email}</small>
                        </div>
                      </td>
                      <td className="fw-bold text-success">
                        {formatCurrency(request.amount)}
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">
                            {request.bank_account?.bank_name || request.bank_name}
                          </div>
                          <small className="text-muted">
                            {request.bank_account?.account_type || request.account_type} - 
                            Ag: {request.bank_account?.agency || request.agency} | 
                            Conta: {request.bank_account?.account_number || request.account_number}
                            {request.bank_account?.account_digit && `-${request.bank_account.account_digit}`}
                          </small>
                        </div>
                      </td>
                      <td>{formatDate(request.created_at)}</td>
                      {activeView === 'approved' && (
                        <td>
                          <Badge bg="success" className="px-2">
                            {formatDate(request.processed_at)}
                          </Badge>
                        </td>
                      )}
                      {activeView === 'rejected' && (
                        <td>
                          <Badge bg="danger" className="px-2">
                            {formatDate(request.processed_at)}
                          </Badge>
                        </td>
                      )}
                      <td className="text-truncate" style={{ maxWidth: '200px' }}>
                        {request.user_notes || '-'}
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => loadRequestDetails(request)}
                            title="Ver detalhes completos"
                          >
                            <Icon icon="mdi:eye" />
                          </Button>
                          {activeView === 'pending' && (
                            <>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowApproveModal(true);
                            }}
                            title="Aprovar saque"
                          >
                            <Icon icon="mdi:check" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowRejectModal(true);
                            }}
                            title="Rejeitar saque"
                          >
                            <Icon icon="mdi:close" />
                          </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
              )}
        </Card.Body>
      </Card>

      {/* Modal de Aprovação */}
      <BootstrapModal show={showApproveModal} onHide={() => setShowApproveModal(false)}>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>
            <Icon icon="mdi:check-circle" className="me-2 text-success" />
            Aprovar Solicitação #{selectedRequest?.id}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <Form>
              <Form.Group className="mb-3">
              <Form.Label>Observações do Administrador (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Adicione observações sobre a aprovação..."
                />
              </Form.Group>
          </Form>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setShowApproveModal(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleApprove} disabled={processing}>
            {processing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Aprovando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="me-1" />
                Aprovar Saque
              </>
            )}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Modal de Rejeição */}
      <BootstrapModal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>
            <Icon icon="mdi:close-circle" className="me-2 text-danger" />
            Rejeitar Solicitação #{selectedRequest?.id}
          </BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <Form>
              <Form.Group className="mb-3">
              <Form.Label>Motivo da Rejeição *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explique o motivo da rejeição..."
                required
                />
              </Form.Group>
          </Form>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={processing || !rejectionReason.trim()}>
            {processing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Rejeitando...
              </>
            ) : (
              <>
                <Icon icon="mdi:close" className="me-1" />
                Rejeitar Saque
              </>
            )}
          </Button>
        </BootstrapModal.Footer>
      </BootstrapModal>

      {/* Drawer de Detalhes */}
      <Drawer
        show={showDetailsDrawer}
        onHide={() => {
          setShowDetailsDrawer(false);
          resetModalStates();
        }} 
        title={`Solicitação #${selectedRequest?.id || ''}`}
        subtitle="Análise completa de transações e valores"
        icon="mdi:file-document-multiple"
        size="lg"
        footer={
          selectedRequest?.status === 'pending' && !loadingDetails && requestDetails ? (
            <>
              <Drawer.Button
                variant="danger"
                onClick={() => {
                  setShowDetailsDrawer(false);
                  setShowRejectModal(true);
                }}
                icon="mdi:close-circle"
              >
                Reprovar
              </Drawer.Button>
              <Drawer.Button
                variant="success"
                onClick={() => {
                  setShowDetailsDrawer(false);
                  setShowApproveModal(true);
                }}
                icon="mdi:check-circle"
              >
                Aprovar Saque
              </Drawer.Button>
            </>
          ) : (
            <Drawer.Button 
              variant="secondary" 
              onClick={() => {
                setShowDetailsDrawer(false);
                resetModalStates();
              }}
            >
              Fechar
            </Drawer.Button>
          )
        }
      >
          {loadingDetails ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="text-muted">Carregando detalhes da solicitação...</p>
            </div>
          ) : requestDetails ? (
            <>
              {/* Resumo Financeiro em Destaque */}
            <Drawer.Section title="Resumo Financeiro" icon="mdi:chart-line">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '4px' }}>
                <Drawer.InfoCard variant="success">
                  <div style={{ textAlign: 'center' }}>
                    <Icon icon="mdi:cash-multiple" size={36} style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>
                        Valor Solicitado
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700' }}>
                      {formatCurrency(requestDetails.request.amount)}
                    </div>
                  </div>
                </Drawer.InfoCard>
                <Drawer.InfoCard variant="info">
                  <div style={{ textAlign: 'center' }}>
                    <Icon icon="mdi:format-list-numbered" size={36} style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px' }}>
                      Total de Transações
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700' }}>
                      {requestDetails.summary.total_transactions}
                    </div>
                  </div>
                </Drawer.InfoCard>
              </div>
            </Drawer.Section>
            <Drawer.Divider />
            
            {/* Informações do Usuário */}
            <Drawer.Section title="Informações do Usuário" icon="mdi:account">
              <Drawer.Row label="Nome" value={requestDetails.user?.name || requestDetails.request.user_name} />
              <Drawer.Row label="Email" value={requestDetails.user?.email || requestDetails.request.user_email} />
              <Drawer.Row label="Data da Solicitação" value={formatDate(requestDetails.request.created_at)} />
            </Drawer.Section>
            <Drawer.Divider />

            {/* Informações Bancárias */}
            <Drawer.Section title="Dados Bancários" icon="mdi:bank">
              <Drawer.Row label="Banco" value={requestDetails.bank_account?.bank_name || '-'} />
              <Drawer.Row label="Tipo de Conta" value={requestDetails.bank_account?.account_type === 'corrente' ? 'Conta Corrente' : requestDetails.bank_account?.account_type === 'poupanca' ? 'Conta Poupança' : requestDetails.bank_account?.account_type || '-'} />
              <Drawer.Row label="Agência" value={requestDetails.bank_account?.agency || '-'} />
              <Drawer.Row label="Conta" value={requestDetails.bank_account ? `${requestDetails.bank_account.account_number}${requestDetails.bank_account.account_digit ? `-${requestDetails.bank_account.account_digit}` : ''}` : '-'} />
            </Drawer.Section>
            <Drawer.Divider />

              {/* Observações */}
              {requestDetails.request.user_notes && (
              <>
                <Drawer.Section title="Observações do Usuário" icon="mdi:note-text">
                  <p className="mb-0">{requestDetails.request.user_notes}</p>
                </Drawer.Section>
                <Drawer.Divider />
              </>
            )}
            
            {/* Resumo Financeiro */}
            <Drawer.Section title="Resumo Financeiro Detalhado" icon="mdi:calculator">
              {requestDetails.request.has_anticipation ? (
                <>
                  <Drawer.Row
                    label="Saldo disponível sacado"
                    value={formatCurrency(requestDetails.request.amount - requestDetails.request.anticipated_amount + requestDetails.request.anticipation_fee)}
                  />
                  <Drawer.Row label="Antecipação (bruto)" value={`+ ${formatCurrency(requestDetails.request.anticipated_amount)}`} />
                  <Drawer.Row label="Taxa de antecipação" value={`- ${formatCurrency(requestDetails.request.anticipation_fee)}`} />
                  {requestDetails.request.anticipation_plan_code && (
                    <Drawer.Row label="Plano" value={requestDetails.request.anticipation_plan_code} />
                  )}
                  <Drawer.Row label="Valor líquido (receberá)" value={formatCurrency(requestDetails.request.net_amount)} highlight />
                </>
              ) : (
                <>
                  <Drawer.Row label="Valor solicitado" value={formatCurrency(requestDetails.request.amount)} />
                  <Drawer.Row label="Valor líquido (receberá)" value={formatCurrency(requestDetails.request.net_amount)} highlight />
                </>
              )}
            </Drawer.Section>
            </>
          ) : (
          <Drawer.EmptyState 
            icon="mdi:alert"
            text="Não foi possível carregar os detalhes da solicitação."
          />
        )}
      </Drawer>
    </>
  );
};

export default WithdrawalAdminPanel;